@if (state === 'form') {
  <div class="card unlock-card container">
    <div class="card-body">
      <img [src]="logoUrl" alt="Logo" class="unlock-logo" />
      <h5 class="card-title">Unlock Your Account</h5>
      <p class="text-muted">Enter your username and we'll send a verification code to your email.</p>
      <form (ngSubmit)="onSubmit()" autocomplete="off">
        <div class="form-group required">
          <label for="unlock-username">Username</label>
          <input
            [(ngModel)]="username"
            type="text"
            class="form-control"
            name="username"
            id="unlock-username"
            required
          />
        </div>
        <button
          type="submit"
          class="btn btn-unlock-primary"
          [thLoadingIndicator]="submitting"
          [disabled]="!username.trim()"
        >
          Send Verification Code
        </button>
        @if (sendError) {
          <div class="alert alert-danger mt-3">{{ sendError }}</div>
        }
      </form>
      <div class="unlock-help-links">
        <button type="button" (click)="backToLogin.emit()">Back to sign in</button>
      </div>
    </div>
  </div>
}

@if (state === 'code-entry') {
  <div class="card unlock-card container">
    <div class="card-body text-center">
      <img [src]="logoUrl" alt="Logo" class="unlock-logo" />
      <div class="mb-3">
        <span class="unlock-icon-circle">
          <i class="fas fa-envelope fa-lg"></i>
        </span>
      </div>
      <h5 class="card-title">Unlock your Account</h5>
      <p class="text-muted text-start">
        If your account is locked, a verification code has been sent to
        @if (maskedEmail) {
          <strong>{{ maskedEmail }}</strong
          >.
        } @else {
          your email.
        }
        If you don't receive a code within a few minutes, your account may not be locked — try
        <button type="button" class="btn btn-link p-0" (click)="backToLogin.emit()">signing in directly</button>. If you
        continue having trouble, contact support at <strong>{{ supportPhone }}</strong> or
        <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a
        >.
      </p>
      <form (ngSubmit)="onVerify()" autocomplete="off" class="text-start">
        <div class="form-group required">
          <label for="unlock-code">Enter Code</label>
          <input
            [(ngModel)]="code"
            type="text"
            inputmode="numeric"
            maxlength="6"
            pattern="[0-9]*"
            class="form-control"
            name="code"
            id="unlock-code"
            required
          />
          @if (verifyError) {
            <small class="text-danger">{{ verifyError }}</small>
          }
        </div>
        <button
          type="submit"
          class="btn btn-unlock-primary"
          [thLoadingIndicator]="submitting"
          [disabled]="!code.trim()"
        >
          Verify
        </button>
      </form>
      <div class="unlock-resend mt-2">
        @if (resendMessage) {
          <small class="text-success d-block mb-1">{{ resendMessage }}</small>
        }
        @if (resendError) {
          <small class="text-danger d-block mb-1">{{ resendError }}</small>
        }
        <button
          type="button"
          class="btn btn-link p-0"
          [disabled]="resending || resendCooldown > 0"
          (click)="onResendCode()"
        >
          @if (resendCooldown > 0) {
            Resend code in {{ resendCooldown }}s
          } @else {
            Resend Code
          }
        </button>
      </div>
      <div class="unlock-help-links">
        @if (!showAlternative) {
          <button type="button" (click)="showAlternative = true">Verify with something else</button>
        } @else {
          <span class="help-link-text">
            Please contact our support team at <strong>{{ supportPhone }}</strong> or
            <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a> for alternative verification options.
          </span>
        }
        <button type="button" (click)="backToLogin.emit()">Back to sign in</button>
      </div>
    </div>
  </div>
}

@if (state === 'reset-password') {
  <div class="card unlock-card container">
    <div class="card-body">
      <img [src]="logoUrl" alt="Logo" class="unlock-logo" />
      <h5 class="card-title">Create Your New Password</h5>
      <p class="text-muted">Choose a new password for your account.</p>
      <div class="input-group mb-2">
        <input type="text" class="form-control" [value]="username" readonly />
        <span class="input-group-text"><i class="fas fa-user"></i></span>
      </div>
      <div class="text-muted small mb-3">
        Password requirements:
        <ul class="mt-1">
          <li>At least 8 characters</li>
          <li>A lowercase letter</li>
          <li>An uppercase letter</li>
          <li>A number</li>
          <li>Cannot be a recently used password</li>
          <li>No parts of your username</li>
        </ul>
      </div>
      <form (ngSubmit)="onResetPassword()">
        <div class="form-group required">
          <label for="new-password">New password</label>
          <div class="input-group">
            <input
              [(ngModel)]="newPassword"
              [type]="showNewPassword ? 'text' : 'password'"
              class="form-control"
              name="newPassword"
              id="new-password"
              autocomplete="new-password"
              required
            />
            <button
              type="button"
              class="btn btn-outline-secondary"
              (click)="showNewPassword = !showNewPassword"
              tabindex="-1"
            >
              <i class="fas" [class.fa-eye]="!showNewPassword" [class.fa-eye-slash]="showNewPassword"></i>
            </button>
          </div>
        </div>
        <div class="form-group required">
          <label for="confirm-password">Re-enter password</label>
          <div class="input-group">
            <input
              [(ngModel)]="confirmPassword"
              [type]="showConfirmPassword ? 'text' : 'password'"
              class="form-control"
              name="confirmPassword"
              id="confirm-password"
              autocomplete="new-password"
              required
            />
            <button
              type="button"
              class="btn btn-outline-secondary"
              (click)="showConfirmPassword = !showConfirmPassword"
              tabindex="-1"
            >
              <i class="fas" [class.fa-eye]="!showConfirmPassword" [class.fa-eye-slash]="showConfirmPassword"></i>
            </button>
          </div>
          @if (passwordMismatch) {
            <small class="text-danger">Passwords do not match.</small>
          }
        </div>
        @if (resetError) {
          <div class="alert alert-danger">{{ resetError }}</div>
        }
        <button
          type="submit"
          class="btn btn-unlock-primary"
          [thLoadingIndicator]="submitting"
          [disabled]="!newPassword || !confirmPassword"
        >
          Reset Password
        </button>
      </form>
    </div>
  </div>
}

@if (state === 'success') {
  <div class="card unlock-card container">
    <div class="card-body text-center">
      <img [src]="logoUrl" alt="Logo" class="unlock-logo" />
      <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
      <h5 class="card-title">Account Unlocked</h5>
      <p class="text-muted">Your account has been unlocked and your password has been reset. You may now sign in.</p>
      <div class="unlock-help-links">
        <button type="button" (click)="backToLogin.emit()">Back to sign in</button>
      </div>
    </div>
  </div>
}

@if (state === 'blocked') {
  <div class="card unlock-card container">
    <div class="card-body text-center">
      <img [src]="logoUrl" alt="Logo" class="unlock-logo" />
      <i class="fas fa-lock fa-3x mb-3 text-danger"></i>
      <h5 class="card-title">Account Recovery Blocked</h5>
      <p class="text-muted">
        You have exceeded the maximum number of verification attempts. Please contact our support team to restore access
        to your account.
      </p>
      <p>
        <strong>{{ supportPhone }}</strong
        ><br />
        <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a>
      </p>
      <div class="unlock-help-links">
        <button type="button" (click)="backToLogin.emit()">Back to sign in</button>
      </div>
    </div>
  </div>
}

@if (state === 'error') {
  <div class="card unlock-card container">
    <div class="card-body text-center">
      <img [src]="logoUrl" alt="Logo" class="unlock-logo" />
      <i class="fas fa-headset fa-3x mb-3 text-unlock-primary"></i>
      <h5 class="card-title">Something Went Wrong</h5>
      <p class="text-muted">We were unable to process your request. Please call our support team for assistance.</p>
      <p>
        <strong>{{ supportPhone }}</strong
        ><br />
        <a [href]="'mailto:' + supportEmail">{{ supportEmail }}</a>
      </p>
      <div class="unlock-help-links">
        <button type="button" (click)="backToLogin.emit()">Back to sign in</button>
      </div>
    </div>
  </div>
}
